import type {
  BuilderQuote,
  BuilderQuoteExclusion,
  BuilderQuoteScope,
  BuilderQuoteScore,
  Scenario,
} from "../types";

export interface ScenarioExport {
  version: number;
  exportedAt: string;
  scenario: Scenario;
}

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null;
}

function finitePositive(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : null;
}

function finiteNonNegative(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : undefined;
}

function normaliseScope(value: unknown): BuilderQuoteScope[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (
      !isRecord(item) ||
      typeof item.itemKey !== "string" ||
      typeof item.label !== "string"
    ) {
      return [];
    }
    const dollarAmount = finiteNonNegative(item.dollarAmount);
    if (dollarAmount === undefined) return [];
    return [
      {
        itemKey: item.itemKey,
        label: item.label,
        dollarAmount,
        ...(item.per === "building" || item.per === "project"
          ? { per: item.per }
          : {}),
        ...(typeof item.buildingId === "string"
          ? { buildingId: item.buildingId }
          : {}),
      },
    ];
  });
}

function normaliseExclusions(value: unknown): BuilderQuoteExclusion[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (
      !isRecord(item) ||
      typeof item.itemKey !== "string" ||
      typeof item.label !== "string" ||
      (item.status !== "included" &&
        item.status !== "excluded" &&
        item.status !== "nil" &&
        item.status !== "silent")
    ) {
      return [];
    }
    const dollarAmount = finiteNonNegative(item.dollarAmount);
    return [
      {
        itemKey: item.itemKey,
        label: item.label,
        status: item.status,
        ...(dollarAmount !== undefined ? { dollarAmount } : {}),
        ...(typeof item.notes === "string" ? { notes: item.notes } : {}),
      },
    ];
  });
}

function scoreValue(value: unknown): 0 | 1 | 2 | 3 | 4 | 5 | null {
  return typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= 5
    ? (value as 0 | 1 | 2 | 3 | 4 | 5)
    : null;
}

function normaliseScore(value: unknown): BuilderQuoteScore | undefined {
  if (!isRecord(value)) return undefined;
  const d1 = scoreValue(value.D1_tradeDetail);
  const d2 = scoreValue(value.D2_quantitiesRates);
  const d3 = scoreValue(value.D3_scheduleAlignment);
  const d4 = scoreValue(value.D4_exclusionsCoverage);
  const d5 = scoreValue(value.D5_sourceVerification);
  const d7 = scoreValue(value.D7_rateAlignment);
  const d8 = scoreValue(value.D8_contingencyTransparency);
  if ([d1, d2, d3, d4, d5, d7, d8].some((score) => score === null)) {
    return undefined;
  }
  const preliminaryFlag =
    value.preliminaryFlag === "no_rlb_benchmark" ||
    value.preliminaryFlag === "no_audit" ||
    value.preliminaryFlag === null
      ? value.preliminaryFlag
      : undefined;
  return {
    D1_tradeDetail: d1!,
    D2_quantitiesRates: d2!,
    D3_scheduleAlignment: d3!,
    D4_exclusionsCoverage: d4!,
    D5_sourceVerification: d5!,
    D7_rateAlignment: d7!,
    D8_contingencyTransparency: d8!,
    ...(preliminaryFlag !== undefined ? { preliminaryFlag } : {}),
  };
}

function normaliseModernBuilderQuote(
  value: JsonRecord,
  index: number,
): BuilderQuote | null {
  const reportedTpc = finitePositive(value.reportedTpc);
  if (
    typeof value.builderName !== "string" ||
    value.builderName.trim().length === 0 ||
    reportedTpc === null
  ) {
    return null;
  }
  const scoreDocumentationDepth = normaliseScore(value.scoreDocumentationDepth);
  return {
    id:
      typeof value.id === "string" && value.id.length > 0
        ? value.id
        : `builder-quote-${index + 1}`,
    builderName: value.builderName,
    bidDate: typeof value.bidDate === "string" ? value.bidDate : "",
    reportedTpc,
    ...(typeof value.excludedDesignFees === "boolean"
      ? { excludedDesignFees: value.excludedDesignFees }
      : {}),
    ...(finiteNonNegative(value.excludedContingencyPct) !== undefined
      ? {
          excludedContingencyPct: finiteNonNegative(
            value.excludedContingencyPct,
          ),
        }
      : {}),
    ...(finiteNonNegative(value.excludedDesignFeesPct) !== undefined
      ? {
          excludedDesignFeesPct: finiteNonNegative(value.excludedDesignFeesPct),
        }
      : {}),
    scope: normaliseScope(value.scope),
    exclusions: normaliseExclusions(value.exclusions),
    ...(scoreDocumentationDepth ? { scoreDocumentationDepth } : {}),
    ...(typeof value.notes === "string" ? { notes: value.notes } : {}),
  };
}

function migrateLegacyBuilderQuote(
  value: unknown,
  index: number,
): BuilderQuote | null {
  if (!isRecord(value)) return null;
  if (
    typeof value.builderName === "string" &&
    value.reportedTpc !== undefined
  ) {
    return normaliseModernBuilderQuote(value, index);
  }

  const reportedTpc =
    finitePositive(value.totalCostInclPrelimsMarginsOHs) ??
    finitePositive(value.totalCost);
  if (typeof value.name !== "string" || reportedTpc === null) return null;

  const scope: BuilderQuoteScope[] = Array.isArray(value.lineItems)
    ? value.lineItems.flatMap((item, lineIndex) => {
        if (!isRecord(item)) return [];
        const dollarAmount =
          finitePositive(item.totalCostInclPrelimsMarginsOHs) ??
          finitePositive(item.cost);
        if (dollarAmount === null) return [];
        const ref =
          typeof item.ref === "number" || typeof item.ref === "string"
            ? String(item.ref)
            : String(lineIndex + 1);
        return [
          {
            itemKey: `legacy-line-${ref}`,
            label:
              typeof item.areaBuilding === "string"
                ? item.areaBuilding
                : `Imported quote line ${ref}`,
            dollarAmount,
            per: "project" as const,
          },
        ];
      })
    : [];

  const basisNote =
    typeof value.costBasisNote === "string" ? value.costBasisNote : "";
  const lowerBasis = basisNote.toLowerCase();
  const exclusions: BuilderQuote["exclusions"] = [];
  if (lowerBasis.includes("contingenc")) {
    exclusions.push({
      itemKey: "contingency",
      label: "Construction contingency",
      status: "excluded",
      notes: basisNote,
    });
  }
  if (lowerBasis.includes("consultant")) {
    exclusions.push({
      itemKey: "consultants",
      label: "Consultants",
      status: "excluded",
      notes: basisNote,
    });
  }

  const importedAt =
    typeof value.importedAt === "string" ? value.importedAt : "";
  const notes = [
    basisNote,
    typeof value.source === "string" ? `Imported from ${value.source}` : "",
  ].filter(Boolean);

  return {
    id:
      typeof value.id === "string" && value.id.length > 0
        ? value.id
        : `legacy-builder-quote-${index + 1}`,
    builderName: value.name,
    bidDate: /^\d{4}-\d{2}-\d{2}/.test(importedAt)
      ? importedAt.slice(0, 10)
      : "",
    reportedTpc,
    scope,
    exclusions,
    notes: notes.join(". "),
  };
}

export function migrateLegacyBuilderQuotes(
  value: unknown,
): BuilderQuote[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.flatMap((quote, index) => {
    const migrated = migrateLegacyBuilderQuote(quote, index);
    return migrated ? [migrated] : [];
  });
}

export function exportScenarioToJson(scenario: Scenario): string {
  const payload: ScenarioExport = {
    version: 1,
    exportedAt: new Date().toISOString(),
    scenario,
  };
  return JSON.stringify(payload, null, 2);
}

export function parseScenarioJson(json: string): ScenarioExport {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (e) {
    throw new Error(
      `Invalid JSON: ${e instanceof Error ? e.message : String(e)}`,
    );
  }
  if (typeof parsed !== "object" || parsed === null)
    throw new Error("Expected a JSON object");
  const p = parsed as Record<string, unknown>;
  if (!p.scenario) throw new Error("Missing scenario field");
  if (p.version !== 1)
    throw new Error(`Unsupported export version: ${p.version}`);

  const rawScenario = p.scenario;
  if (!isRecord(rawScenario)) {
    return parsed as ScenarioExport;
  }
  const rawBuildings = rawScenario.buildings;
  const buildings = Array.isArray(rawBuildings)
    ? rawBuildings.map((building) => {
        if (!isRecord(building) || building.constructionPerSqmGfa !== null) {
          return building;
        }
        return { ...building, constructionPerSqmGfa: 0 };
      })
    : rawBuildings;
  const rawBuilderQuotes = rawScenario.builderQuotes;
  const builderQuotes = migrateLegacyBuilderQuotes(rawBuilderQuotes);

  return {
    ...(parsed as ScenarioExport),
    scenario: {
      ...(rawScenario as unknown as Scenario),
      ...(Array.isArray(buildings)
        ? { buildings: buildings as Scenario["buildings"] }
        : {}),
      ...(builderQuotes ? { builderQuotes } : {}),
    },
  };
}
